import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type { TopologyPresetEntry, TunnelCatalogEntry } from '../api/types.ts';
import { Checkbox, ChoiceGroup, Field, Notice, TextInput } from '../components/ui.tsx';
import type { WizardState } from './state.ts';

/**
 * Settings.
 *
 * In easy mode this is one field — the domain — because everything else has a
 * right answer the platform can work out. Expert mode exposes the tunnel and
 * the port, with "choose for me" still first in the list and marked as the
 * recommendation.
 */
export function OptionsStep({
  tunnels,
  presets,
  state,
  onChange,
}: {
  tunnels: readonly TunnelCatalogEntry[];
  presets: readonly TopologyPresetEntry[];
  state: WizardState;
  onChange: (state: WizardState) => void;
}): ReactNode {
  const { t, pick } = useI18n();
  const preset = presets.find((entry) => entry.preset === state.preset);
  // A single-server setup has nothing to tunnel between, so the question is
  // not asked rather than asked and ignored.
  const hasTunnel = (preset?.slots.length ?? 0) > 1;
  const expert = state.mode === 'expert';

  const domainInvalid =
    state.domain !== '' &&
    !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(state.domain);

  return (
    <div className="form-grid">
      <Field
        label={t('wizard.options.domain')}
        help={t('wizard.options.domainHelp')}
        error={domainInvalid ? t('wizard.options.domainHelp') : undefined}
      >
        {(props) => (
          <TextInput
            {...props}
            className="ltr-text"
            value={state.domain}
            placeholder="panel.example.com"
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => onChange({ ...state, domain: event.target.value.trim() })}
          />
        )}
      </Field>

      {state.domain && !domainInvalid ? (
        <Checkbox
          checked={state.requestSsl}
          onChange={(requestSsl) => onChange({ ...state, requestSsl })}
          label={t('wizard.options.ssl')}
        />
      ) : null}

      {expert && hasTunnel ? (
        <ChoiceGroup
          legend={t('wizard.options.tunnel')}
          value={state.tunnel === 'automatic' ? 'automatic' : state.tunnel.adapterId}
          columns={1}
          onChange={(value) =>
            onChange({
              ...state,
              tunnel: value === 'automatic' ? 'automatic' : { adapterId: value },
            })
          }
          options={[
            {
              value: 'automatic',
              title: t('wizard.options.tunnelAutomatic'),
              description: t('wizard.options.tunnelAutomaticHelp'),
              recommended: true,
            },
            ...tunnels.map((tunnel) => ({
              value: tunnel.adapterId,
              title: tunnel.name,
              description: pick(tunnel.summary),
              meta: `${tunnel.family} · ${tunnel.maturity}`,
            })),
          ]}
        />
      ) : null}

      {expert ? (
        <>
          {/* Easy mode explains itself; expert mode said nothing, so the
              customer who switched had no statement of what they had taken
              on. Both sentences were written; only one was shown. */}
          <Notice tone="neutral">{t('mode.expertHelp')}</Notice>
          <Field label={t('wizard.options.panelPort')} help={t('wizard.options.panelPortHelp')}>
            {(props) => (
              <TextInput
                {...props}
                className="ltr-text"
                type="number"
                min={1}
                max={65535}
                value={state.panelPort}
                onChange={(event) => onChange({ ...state, panelPort: event.target.value })}
              />
            )}
          </Field>

          {hasTunnel ? (
            <Checkbox
              checked={state.requireEncryptedTunnel}
              onChange={(requireEncryptedTunnel) => onChange({ ...state, requireEncryptedTunnel })}
              label={t('wizard.options.encryptedOnly')}
            />
          ) : null}
        </>
      ) : (
        <Notice tone="neutral">{t('mode.easyHelp')}</Notice>
      )}
    </div>
  );
}
